<template>
  <div>
    <v-dialog v-model="internalDialog" max-width="90%" persistent scrollable>
      <v-card class="ma-4" style="overflow-x: hidden; position: relative">
        <!-- Header -->
        <v-card-title
          class="py-6 px-4 primary white--text justify-space-between"
          style="position: sticky; top: 0; z-index: 5; background-color: #000"
        >
          <div>
            <h4>
              Återkopplingsrapport till respondenter av rapport:
              {{ survey.show_id }} "{{ survey.name }}" Svarande:
              {{ survey.nbr_of_answered }} Tillfrågade:
              {{ survey.nbr_of_respondents }}
            </h4>
          </div>
          <v-btn icon @click="close" class="white--text">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Intro -->
        <v-card-text class="px-6 my-4">
          <p class="text-subtitle-1">
            Skriv det meddelande som skall introducera det efterföljande resultatet av
            undersökningen
          </p>
          <ul>
            <li>Rapport: {{ survey.id }} "{{ survey.name }}"</li>
            <li>Svarande: {{ survey.nbr_of_answered }}</li>
            <li>Tillfrågade: {{ survey.nbr_of_respondents }}</li>
          </ul>
        </v-card-text>

        <v-container fluid class="px-6">
          <!-- Meddelande -->
          <v-tiptap
            v-model="message"
            placeholder="Skriv ditt meddelande här"
            ref="editor"
            style="width: 100%; min-height: 200px"
          />

          <!-- Mottagare + Giltigt till -->
          <div class="recipient-time-row">
            <div class="recipient-left">
              <v-radio-group v-model="recipientOption" label="Välj mottagare:">
                <v-radio
                  label="Skicka till alla respondenter i undersökningen"
                  value="all"
                />
                <v-radio
                  label="Skicka bara till de respondenter som svarade på undersökningen"
                  value="answered"
                />
                <v-radio
                  :label="`Skicka bara till manuellt utvalda (just nu ${selectedUsers.length} st)`"
                  value="no-one"
                />
              </v-radio-group>

              <v-btn color="primary" class="mt-2" @click="chooseResponders = true">
                Välj respondenter manuellt...
              </v-btn>
            </div>

            <div class="recipient-right">
              <v-card class="valid-card pa-2" outlined>
                <v-tooltip right open-on-hover close-delay="0" open-delay="100">
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      class="text-h6 mb-3 d-inline-block"
                      v-bind="attrs"
                      v-on="on"
                      tabindex="0"
                      style="cursor: help"
                    >
                      Giltigt till kl:
                    </span>
                  </template>

                  <span>Meddelandet försvinner hos respondenten efter denna tid</span>
                </v-tooltip>

                <v-datetime-picker
                  class="pt-4 mt-2"
                  label="Datum och tid"
                  v-model="validUntilTime"
                  time-format="HH:mm"
                  :time-picker-props="timeProps"
                  clear-text="Avbryt"
                  :text-field-props="dateTimeTextFieldProps"
                  @input="dateTimeGiven"
                  ref="dateTimeRef"
                />
              </v-card>
            </div>
          </div>

          <!-- Dialog för manuell urval -->
          <v-dialog
            v-model="chooseResponders"
            max-width="90vw"
            max-height="90vh"
            persistent
          >
            <v-card
              class="pa-0"
              style="height: 90vh; width: 90vw; display: flex; flex-direction: column"
            >
              <v-card-title class="d-flex justify-space-between align-center">
                <span class="text-h5 font-weight-bold">Respondenter</span>
                <v-btn icon @click="chooseResponders = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>

              <v-card-text style="overflow-y: auto; flex: 1">
                <RespondersList
                  :survey="survey"
                  ref="respondersList"
                  @respondersListClicked="responderClicked"
                />
              </v-card-text>

              <v-card-actions class="justify-center">
                <v-btn color="blue darken-1" text @click="chooseResponders = false"
                  >Avbryt</v-btn
                >
                <v-btn color="blue darken-1" text @click="onConfirm" :disabled="false"
                  >OK</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-container>

        <v-divider class="my-4" />

        <!-- Grafer -->
        <v-container fluid>
          <h3 class="section-header">Välj vilka grafer som ska ingå i utskicket</h3>

          <!-- === Enstaka (bar) === -->
          <template v-if="isBarMode">
            <v-row dense>
              <v-col
                v-for="(quest, index) in graphs"
                :key="index"
                cols="12"
                md="6"
                class="pa-2"
              >
                <v-card outlined class="pa-2 h-100">
                  <h3 class="mb-1">{{ quest.barchart.bar_options.chart.id }}</h3>

                  <v-checkbox
                    v-model="excludedGraphIndices"
                    label="Ta bort i utskick"
                    :value="index"
                    hide-details
                    class="mb-2"
                  />

                  <div v-if="!excludedGraphIndices.includes(index)" class="graph-content">
                    <apexchart
                      width="100%"
                      height="250"
                      type="bar"
                      :options="getBarOptions(quest)"
                      :series="quest.barchart.bar_series"
                    />
                    <div v-if="quest.showLegend" class="text-caption mt-2">
                      <div
                        v-for="(textLegend, i3) in quest.barchart.bar_options.xaxis
                          .categories"
                        :key="i3"
                      >
                        {{ i3 + 1 }}. {{ textLegend }}
                      </div>
                    </div>
                    <div class="mt-2 text-subtitle-2">
                      Antal besvarade: {{ quest.metadata.nbr_of_respondent_answers }} /
                      {{ quest.metadata.total_nbr_of_respondents }}
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </template>

          <!-- === Repeterande (SurveyCharts) === -->
          <template v-else>
            <v-row dense>
              <v-col
                v-for="(rep, rIdx) in repeatReports"
                :key="rep.id || rIdx"
                cols="12"
                md="6"
                class="graph-container mb-6"
              >
                <h3 class="mb-1">
                  {{
                    rep.chartOptions?.yaxis?.title?.text ||
                    rep.title ||
                    `Resultat ${rIdx + 1}`
                  }}
                </h3>

                <v-checkbox
                  v-model="excludedRepeatIndices"
                  label="Ta bort i utskick"
                  :value="rIdx"
                  hide-details
                  class="mb-2"
                />

                <div v-if="!excludedRepeatIndices.includes(rIdx)" class="graph-content">
                  <SurveyCharts
                    :reports="[rep]"
                    :baseTemplate="baseTemplate"
                    :noGrid="true"
                  />
                </div>
              </v-col>
            </v-row>
          </template>
        </v-container>

        <!-- Skicka -->
        <v-card-actions class="d-flex justify-center mt-6 mb-4">
          <v-btn class="pa-4 mx-2" color="blue darken-1" text @click="close"
            >Avbryt</v-btn
          >
          <v-btn class="pa-4 mx-2" color="blue darken-1" text @click="openConfirmDialog">
            Skicka...
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bekräftelse -->
    <v-dialog v-model="confirmDialog" max-width="500">
      <v-card>
        <v-card-title class="headline"> Bekräfta utskick </v-card-title>
        <v-card-text>
          Är du säker på att du vill skicka rapporten till
          {{
            recipientOption === "answered"
              ? survey.nbr_of_answered + this.selectedUsers.length
              : recipientOption === "all"
              ? survey.nbr_of_respondents + this.selectedUsers.length
              : this.selectedUsers.length
          }}
          mottagare?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="blue darken-1" @click="confirmDialog = false">Avbryt</v-btn>
          <v-btn text color="blue darken-1" @click="confirmSend">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ApexChart from "vue-apexcharts";
import RespondersList from "./RespondersList.vue";
import SurveyCharts from "@/components/SurveyCharts.vue";

export default {
  name: "RespondentFeedbackDialog",
  components: {
    apexchart: ApexChart,
    RespondersList,
    SurveyCharts,
  },
  props: {
    value: Boolean,
    survey: Object,
    graphs: Array,
    repeatReports: Array,
    baseTemplate: Object,
    startDate: String,
    endDate: String,
  },
  data() {
    return {
      internalDialog: this.value,
      message: "",
      excludedGraphIndices: [],
      excludedRepeatIndices: [],
      recipientOption: "all",
      confirmDialog: false,
      chooseResponders: false,
      selectedUsers: [],

      // 🕒 Giltigt till-hantering
      validUntilTime: this.$moment().add(4, "days").format("YYYY-MM-DD HH:mm"),
      timeProps: { format: "24hr" },
      dateTimeTextFieldProps: { outlined: true, dense: true },
    };
  },
  computed: {
    isBarMode() {
      return (this.survey?.nbr_of_repetitions ?? 0) <= 0;
    },
  },
  watch: {
    value(val) {
      this.internalDialog = val;
    },
    internalDialog(val) {
      this.$emit("input", val);
    },
  },
  methods: {
    close() {
      this.internalDialog = false;
    },
    openConfirmDialog() {
      this.confirmDialog = true;
    },
    confirmSend() {
      const payload = {
        survey_instance_id: Number(this.survey.id),
        excluded_indices: this.isBarMode
          ? this.excludedGraphIndices
          : this.excludedRepeatIndices,
        // excluded_indices_repeat: this.isBarMode ? [] : this.excludedRepeatIndices,
        text: this.message || " ",
        send_to_recipients: this.recipientOption,
        importance: 3,
        send_at: this.$moment().format("YYYY-MM-DD HH:mm"),
        valid_to:
          this.validUntilTime || this.$moment().add(4, "days").format("YYYY-MM-DD HH:mm"),
        send_to: this.selectedUsers.map((item) => item.id),
        start_at: this.startDate,
        end_at: this.endDate,
      };
      this.$emit("send", payload);
      this.confirmDialog = false;
      this.close();
    },
    responderClicked() {
      this.selectedUsers = this.$refs.respondersList.getSelectedArray();
      console.log("responderClicked", this.selectedUsers);
    },
    onConfirm() {
      this.chooseResponders = false;
    },
    getBarOptions(quest) {
      const maxLen = Math.max(
        ...quest.barchart.bar_options.xaxis.categories.map((c) => c.length)
      );
      quest.showLegend = maxLen >= 23;
      const copy = JSON.parse(JSON.stringify(quest.barchart.bar_options));
      if (quest.showLegend) {
        copy.xaxis.categories = Array.from(
          { length: copy.xaxis.categories.length },
          (_, i) => i + 1
        );
      }
      return copy;
    },
    dateTimeGiven(value) {
      this.validUntilTime = this.$moment(value).format("YYYY-MM-DD HH:mm");
    },
    setTimeFast() {
      this.validUntilTime = this.$moment().add(4, "days").format("YYYY-MM-DD HH:mm");
    },
  },
};
</script>

<style scoped>
.graph-container {
  overflow: visible;
  max-height: none;
  border-bottom: 1px solid #ccc;
  padding-left: 24px;
  padding-bottom: 24px;
}
.graph-content {
  min-height: 350px;
  overflow: visible;
}
.section-header {
  color: #555;
  background-color: #f5f5f5;
  padding: 12px 16px;
  border-bottom: 1px solid #ccc;
  margin-bottom: 16px;
  font-weight: 500;
  text-align: center;
}
.recipient-time-row {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start; /* lägger kortet i höjd med rubriken "Välj mottagare" */
  margin-top: 16px;
  position: relative;
}

.recipient-left {
  flex: 1 1 50%;
  min-width: 0;
}

.recipient-right {
  position: absolute;
  top: 18px;
  left: 50%; /* starta kortet vid mitten av dialogen */
  /* transform: translateX(-15%); /* flytta in kortet lite mot mitten */
}

.valid-card {
  width: 260px;
  min-width: 240px;
  pointer-events: auto !important;
  position: relative;
  z-index: 1;
}
</style>
